/**
 * Holdings List Page
 *
 * Displays all mutual fund holdings with the HoldingCard component.
 * Uses actual data from the BFF API.
 */

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PullToRefresh } from "@/components/ui/pull-to-refresh";
import {
  HoldingCard,
  HoldingCardSkeleton,
  type HoldingCardData,
} from "@/components/holdings";
import { useHoldings, usePullToRefresh, getHoldingsErrorMessage } from "@/hooks";
import type { EnrichedHolding } from "@/hooks";
import { Briefcase, Plus, RefreshCw, AlertCircle } from "lucide-react";
import { GlobalErrorFallback } from "@/components/error";

export const Route = createFileRoute("/holdings/")({
  component: HoldingsPage,
  errorComponent: GlobalErrorFallback,
});

// ============================================================================
// Data Transformation
// ============================================================================

/**
 * Transform enriched holding to HoldingCardData
 */
function transformToHoldingCardData(
  holding: EnrichedHolding,
  totalPortfolioValue: number
): HoldingCardData | null {
  if (!holding.fund) return null;

  const allocationPercent =
    totalPortfolioValue > 0
      ? (holding.currentValue / totalPortfolioValue) * 100
      : 0;

  return {
    id: holding.id,
    fundName: holding.fund.name,
    category: holding.fund.category,
    amcName: holding.fund.amcName,
    currentValue: holding.currentValue,
    totalReturns: holding.totalReturns,
    totalReturnsPercent: holding.totalReturnsPercent,
    returns1Y: holding.fund.returns1Y,
    categoryAverageReturns: undefined,
    rank: holding.fund.rank,
    totalFundsInCategory: holding.fund.totalFundsInCategory,
    rating: holding.fund.rating,
    // consistencyScore is only available in holding detail view, not in list view
    allocationPercent,
  };
}

// ============================================================================
// Skeleton Component
// ============================================================================

function HoldingsPageSkeleton() {
  return (
    <div className="container mx-auto max-w-3xl px-4 py-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <Skeleton className="h-8 w-48" />
          <Skeleton className="mt-2 h-4 w-64" />
        </div>
        <Skeleton className="h-10 w-32" />
      </div>
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <HoldingCardSkeleton key={i} />
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Error Component
// ============================================================================

function ErrorState({
  message,
  onRetry,
  isRetrying,
}: {
  message: string;
  onRetry: () => void;
  isRetrying: boolean;
}) {
  return (
    <div className="container mx-auto max-w-3xl px-4 py-6">
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-8 text-center">
        <AlertCircle className="mx-auto h-12 w-12 text-destructive" />
        <h3 className="mt-4 text-lg font-semibold text-destructive">
          Failed to load holdings
        </h3>
        <p className="mt-2 text-sm text-muted-foreground">{message}</p>
        <Button
          variant="outline"
          onClick={onRetry}
          disabled={isRetrying}
          className="mt-4"
        >
          {isRetrying ? (
            <>
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              Retrying...
            </>
          ) : (
            <>
              <RefreshCw className="mr-2 h-4 w-4" />
              Try Again
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Empty State Component
// ============================================================================

function EmptyState() {
  return (
    <div className="rounded-lg border border-dashed border-border bg-muted/30 p-12 text-center">
      <Briefcase className="mx-auto h-16 w-16 text-muted-foreground" />
      <h3 className="mt-4 text-xl font-semibold text-foreground">
        No holdings yet
      </h3>
      <p className="mt-2 text-muted-foreground">
        Start by adding your first mutual fund holding to track your investments.
      </p>
      <Button className="mt-6">
        <Plus className="mr-2 h-4 w-4" />
        Add Your First Holding
      </Button>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

function HoldingsPage() {
  const navigate = useNavigate();

  // Fetch holdings data
  const {
    data: holdingsData,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useHoldings();

  // Pull to refresh
  const { isRefreshing, handleRefresh } = usePullToRefresh({
    onRefresh: refetch,
    isFetching,
  });

  // Navigate to holding detail
  const handleHoldingClick = (holdingId: string) => {
    navigate({ to: "/holdings/$holdingId", params: { holdingId } });
  };

  // Show skeleton while loading
  if (isLoading) {
    return <HoldingsPageSkeleton />;
  }

  // Show error state
  if (isError) {
    return (
      <ErrorState
        message={getHoldingsErrorMessage(error)}
        onRetry={() => refetch()}
        isRetrying={isFetching}
      />
    );
  }

  // Transform holdings data
  const holdings = holdingsData?.holdings ?? [];
  // Compute totalPortfolioValue from holdings to ensure consistency with displayed values
  const totalPortfolioValue = holdings.reduce((sum, h) => sum + h.currentValue, 0);

  const holdingCards: HoldingCardData[] = holdings
    .map((h) => transformToHoldingCardData(h, totalPortfolioValue))
    .filter((h): h is HoldingCardData => h !== null);

  return (
    <PullToRefresh
      onRefresh={handleRefresh}
      isRefreshing={isRefreshing}
      className="h-full"
    >
      <div className="container mx-auto max-w-3xl px-4 py-6">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Your Holdings</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {holdingCards.length} mutual fund{holdingCards.length !== 1 ? "s" : ""} in your portfolio
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRefresh}
              disabled={isRefreshing || isFetching}
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing || isFetching ? "animate-spin" : ""}`} />
            </Button>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add Holding
            </Button>
          </div>
        </div>

        {/* Holdings List */}
        {holdingCards.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="space-y-3">
            {holdingCards.map((holding) => (
              <HoldingCard
                key={holding.id}
                data={holding}
                onClick={handleHoldingClick}
              />
            ))}
          </div>
        )}
      </div>
    </PullToRefresh>
  );
}
